/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import template from './report-translation-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {LOCALES} from '../common/constants/locales';
import {
  buildReportTranslationOverrides,
  getActiveReportTranslationOverrides,
  initReportTranslationModel,
  prepareReportTranslationForSubmit,
  REPORT_TRANSLATION_LANGUAGES,
  ReportTranslationOverride,
  ReportTranslationOverrideMap
} from './report-translation-commons';
import {ReportTranslationApiService, ReportTranslationModel} from './report-translation-api.service';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {APP_SHARED} from '../common/app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, TranslateModule, ButtonsModule],
  selector: 'adn-report-translation-edit-page',
  template
})
export class ReportTranslationEditPageComponent extends SignalAdminResourceEditPageBase<ReportTranslationModel> {
  readonly locales = LOCALES;
  readonly languages = REPORT_TRANSLATION_LANGUAGES;

  translations: ReportTranslationOverrideMap = {};
  readonly translationsLoaded = signal(false);
  activeOverrides: ReportTranslationOverride[] = [];

  constructor(
    private readonly reportTranslationApi: ReportTranslationApiService
  ) {
    super(reportTranslationApi, 'reportTranslation');
  }

  private readonly componentDestroyRef = inject(DestroyRef);

  languageLabel(language: string): string {
    if (language === 'EN') {
      return 'English';
    }
    if (language === 'DE') {
      return 'German';
    }
    return 'French';
  }

  onDefaultTranslationChange(): void {
    this.syncActiveOverrides();
    this.markExternalFormDirty();
  }

  protected override initialiseModel(model: ReportTranslationModel): ReportTranslationModel {
    const initialised = super.initialiseModel(model);
    initReportTranslationModel(initialised);
    this.loadTranslationDefaults();
    return initialised;
  }

  protected override prepareForSubmit(model: ReportTranslationModel): void {
    prepareReportTranslationForSubmit(model, this.translations);
  }

  private loadTranslationDefaults(): void {
    this.translationsLoaded.set(false);
    this.reportTranslationApi.getDefaults().pipe(
      takeUntilDestroyed(this.componentDestroyRef)
    ).subscribe((transDefaults) => {
      this.translations = buildReportTranslationOverrides(this.editingModel, transDefaults || {});
      this.syncActiveOverrides();
      this.translationsLoaded.set(true);
    });
  }

  private syncActiveOverrides(): void {
    this.activeOverrides = getActiveReportTranslationOverrides(this.translations, this.editingModel.defaultTranslation);
  }
}
